import {
  getTrashCount,
  getTrashedBenefits,
  getTrashedCircuits,
  restoreBenefits,
  restoreCircuits,
  TrashParams,
} from '@/api/trash.api';
import { invalidateBenefitLists } from '@/hooks/useBenefits';
import { invalidateCircuitLists } from '@/hooks/useCircuits';
import { queryKeys } from '@/queryKeys';
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

// Under the trash key, so every deletion and restore refreshes the badge.
export const useGetTrashCount = () =>
  useQuery({ queryKey: queryKeys.trash.count, queryFn: getTrashCount });

export const useGetTrashedBenefits = (params: TrashParams) =>
  useQuery({
    queryKey: queryKeys.trash.benefits(params),
    queryFn: () => getTrashedBenefits(params),
    placeholderData: keepPreviousData,
  });

export const useGetTrashedCircuits = (params: TrashParams) =>
  useQuery({
    queryKey: queryKeys.trash.circuits(params),
    queryFn: () => getTrashedCircuits(params),
    placeholderData: keepPreviousData,
  });

// A restored benefit goes back into the circuits and hotels it was removed
// from, so both lists are refreshed along with the trash.
export const useRestoreBenefits = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ benefitIds }: { benefitIds: string[] }) =>
      restoreBenefits(benefitIds),
    onSuccess: () => {
      invalidateBenefitLists(queryClient);
      invalidateCircuitLists(queryClient);
    },
  });
};

export const useRestoreCircuits = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ circuitIds }: { circuitIds: string[] }) =>
      restoreCircuits(circuitIds),
    onSuccess: () => {
      invalidateCircuitLists(queryClient);
    },
  });
};
